import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { authStore } from '../store/auth-store';
import { request } from '../api/request';
import { LoginResult } from '../types';

/**
 * Modern Minimalist Login View
 * 根据用户输入 API Key (Tenant ID) 进行登录。
 * 使用 Shadow DOM 封装避免影响全局。
 */
@customElement('login-view')
export class LoginView extends LitElement {
  static styles = css`
    :host {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100vh;
      background: linear-gradient(135deg, #f0f4f8, #d9e2ec);
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    }

    .login-card {
      background: white;
      padding: 3rem;
      border-radius: 16px;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05), 0 20px 48px rgba(0, 0, 0, 0.05);
      width: 100%;
      max-width: 400px;
      transition: transform 0.3s ease;
    }

    .login-card:hover {
      transform: translateY(-2px);
    }

    .header {
      text-align: center;
      margin-bottom: 2rem;
    }

    .header h2 {
      margin: 0;
      color: #102a43;
      font-size: 1.75rem;
      font-weight: 600;
    }

    .header p {
      color: #627d98;
      margin-top: 0.5rem;
      font-size: 0.95rem;
    }

    .input-group {
      margin-bottom: 2rem;
    }

    .input-group label {
      display: block;
      margin-bottom: 0.5rem;
      color: #334e68;
      font-size: 0.875rem;
      font-weight: 500;
    }

    .input-group input {
      width: 100%;
      padding: 0.75rem 1rem;
      border-radius: 8px;
      border: 1px solid #bcccdc;
      font-size: 1rem;
      outline: none;
      transition: all 0.2s;
      box-sizing: border-box;
    }

    .input-group input:focus {
      border-color: #2684ff;
      box-shadow: 0 0 0 3px rgba(38, 132, 255, 0.2);
    }

    .submit-btn {
      width: 100%;
      padding: 0.875rem;
      border-radius: 8px;
      border: none;
      background-color: #102a43;
      color: white;
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.2s;
    }

    .submit-btn:hover {
      background-color: #243b53;
    }

    .submit-btn:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(16, 42, 67, 0.3);
    }

    .submit-btn:active {
      background-color: #0b1c2d;
    }

    .error-msg {
      color: #d64545;
      font-size: 0.875rem;
      margin-top: 0.5rem;
      min-height: 1.2rem;
    }
  `;

  @state() private usernameInput = '';
  @state() private passwordInput = '';
  @state() private errorMsg = '';
  @state() private isLoggingIn = false;

  private handleInput(field: 'username' | 'password', e: Event) {
    const target = e.target as HTMLInputElement;
    if (field === 'username') this.usernameInput = target.value;
    else this.passwordInput = target.value;
    this.errorMsg = '';
  }

  private async handleSubmit(e: Event) {
    e.preventDefault();
    if (!this.usernameInput.trim() || !this.passwordInput.trim()) {
      this.errorMsg = '请输入有效的用户名和密码';
      return;
    }

    this.isLoggingIn = true;
    try {
      // 请求后端 login 接口
      const result = await request<LoginResult>('/api/v1/users/login', {
        method: 'POST',
        body: JSON.stringify({
          username: this.usernameInput.trim(),
          password: this.passwordInput.trim()
        })
      });

      // 执行登录状态保存
      authStore.login(result.token, result.user.api_key, result.user.username, result.user.role);
    } catch (err: unknown) {
      if (err instanceof Error) {
        this.errorMsg = err.message;
      }
    } finally {
      this.isLoggingIn = false;
    }
  }

  render() {
    return html`
      <div class="login-card">
        <div class="header">
          <h2>机器人云平台</h2>
          <p>客户服务与设备管理门户</p>
        </div>
        <form @submit=${this.handleSubmit}>
          <div class="input-group">
            <label for="username">用户名</label>
            <input 
              id="username" 
              type="text" 
              placeholder="请输入管理员或租户用户名" 
              .value=${this.usernameInput}
              @input=${(e: Event) => this.handleInput('username', e)}
              autocomplete="username"
            />
          </div>
          <div class="input-group">
            <label for="password">密码</label>
            <input 
              id="password" 
              type="password" 
              placeholder="请输入密码" 
              .value=${this.passwordInput}
              @input=${(e: Event) => this.handleInput('password', e)}
              autocomplete="current-password"
            />
            <div class="error-msg">${this.errorMsg}</div>
          </div>
          <button type="submit" class="submit-btn" ?disabled=${this.isLoggingIn}>
            ${this.isLoggingIn ? '登录中...' : '登录系统'}
          </button>
        </form>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'login-view': LoginView;
  }
}
